import { Text } from "@asayinc/component-library";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import { LoadingCard } from "src/components/molecules/Loading";
import { useNoboEventQuery } from "src/store/noboEvent";

export function NoboEventStatsTab() {
  const { id } = useParams() as { id: string };
  const { data: noboEvent, isLoading, error } = useNoboEventQuery({ id });

  if (error || (!isLoading && !noboEvent)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const { noboEventStats, stats } = noboEvent;
  const {
    eligibleParentBrokers,
    eligibleBrokers,
    respondedParentBrokers,
    awaitingParentBrokers,
    totalPositions,
    totalCustomers,
    totalShares,
    giftedPositions,
    giftedCustomers,
    giftedShares,
    fullSharePositions,
    fullShareCustomers,
    fullShareShares,
    nonFullSharePositions,
    nonFullShareCustomers,
    nonFullShareShares,
    invoiceDate,
    invoiceAmount,
    estimatedInvoiceAmount,
  } = noboEventStats || {};

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Box sx={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
        {noboEventStats ? (
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <Card title="Broker Stats">
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: 600,
                }}
                isLabelBold={true}
                label="Eligible Parent Brokers"
                value={eligibleParentBrokers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Eligible Brokers"
                value={eligibleBrokers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                  fontWeight: 600,
                }}
                isLabelBold={true}
                label="Responded Parent Brokers"
                value={respondedParentBrokers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Awaiting Parent Brokers"
                value={awaitingParentBrokers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  fontWeight: 600,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isLabelBold={true}
                label="Total Customers"
                value={totalCustomers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Full Share Customers"
                value={fullShareCustomers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Fractional Customers"
                value={nonFullShareCustomers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Gifted Customers"
                value={giftedCustomers.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  fontWeight: 600,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isLabelBold={true}
                label="Total Positions"
                value={totalPositions.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Full Share Positions"
                value={fullSharePositions.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Fractional Positions"
                value={nonFullSharePositions.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Gifted Positions"
                value={giftedPositions.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  fontWeight: 600,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isLabelBold={true}
                label="Total Shares"
                value={totalShares.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Full Share Shares"
                value={fullShareShares.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Fractional Shares"
                value={nonFullShareShares.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Gifted Shares"
                value={giftedShares.toString()}
              />
            </Card>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <Card title="Broker Stats">
              <Text
                variant="body2"
                sx={{ color: "text.secondary", py: 5, px: 4 }}
              >
                No stats available
              </Text>
            </Card>
          </Box>
        )}
        {stats ? (
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: 0,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Card title="Position Breakdown">
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isLabelBold={true}
                label="Total Shares"
                value={parseFloat(stats.totalShares).toLocaleString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  px: 4,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isLabelBold={true}
                label="Position Count"
                value={stats.positionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Billable Position Count"
                value={stats.billablePositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Gifted Positions"
                value={stats.giftedPositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 18,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Full Share Gifted Positions"
                value={stats.fullShareGiftedPositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 18,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Fractional Gifted Positions"
                value={stats.nonFullShareGiftedPositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 9,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Non-Gifted Positions"
                value={stats.nonGiftedPositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 18,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Full Share Non-Gifted Positions"
                value={stats.fullShareNonGiftedPositionCount.toString()}
              />
              <LabelField
                sx={{
                  py: 5,
                  pr: 4,
                  pl: 4,
                  ml: 18,
                  borderBottom: "1px solid #e0e0e0",
                }}
                isValueBold={false}
                label="Fractional Non-Gifted Positions"
                value={stats.nonFullShareNonGiftedPositionCount.toString()}
              />
            </Card>
            {noboEventStats ? (
              <Card title="Invoice Information">
                <LabelField
                  sx={{
                    py: 5,
                    px: 4,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  isLabelBold={true}
                  label="Invoice date"
                  value={
                    invoiceDate
                      ? dayjs(invoiceDate).format("MM/DD/YYYY")
                      : "N/A"
                  }
                />
                <LabelField
                  sx={{
                    py: 5,
                    px: 4,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  isLabelBold={true}
                  label="Invoice amount"
                  value={`$${(invoiceAmount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`}
                />
                <LabelField
                  sx={{
                    py: 5,
                    px: 4,
                    borderBottom: "1px solid #e0e0e0",
                  }}
                  isLabelBold={true}
                  label="Estimated invoice amount"
                  value={`$${(estimatedInvoiceAmount || 0).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}`}
                />
              </Card>
            ) : (
              <Card title="Invoice Information">
                <Text
                  variant="body2"
                  sx={{ color: "text.secondary", py: 5, px: 4 }}
                >
                  No invoice stats available
                </Text>
              </Card>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              flexBasis: 0,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <Card title="Position Breakdown">
              <Text
                variant="body2"
                sx={{ color: "text.secondary", py: 5, px: 4 }}
              >
                No position stats available
              </Text>
            </Card>
            <Card title="Invoice Information">
              <Text
                variant="body2"
                sx={{ color: "text.secondary", py: 5, px: 4 }}
              >
                No invoice stats available
              </Text>
            </Card>
          </Box>
        )}
      </Box>
    </ScrollContainer>
  );
}
